import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';

//Redux
import {
  setStepSent,
  setStepContextVariables,
  setStepFileNames,
} from 'actions/workflows/workflows';
import { motion } from 'motion/react';

import {
  selectWorkflowTags,
  selectAreLoadingDotsVisible,
} from 'reducers/workflows';

// Components
import OpenUrl from 'components/OpenUrlStep/OpenUrlStep';
import ZendeskAttachmentUploadStep from 'components/ZendeskAttachmentUploadStep/ZendeskAttachmentUploadStep';
import MessageStep from 'components/MessageStep/MessageStep';
import SingleSelect from 'components/SingleSelect/SingleSelect';
import FormStep from 'components/FormStep/FormStep';
import ArticleStep from 'components/ArticleStep/ArticleStep';
import ArticleCarousel from 'components/ArticleCarousel/ArticleCarousel';
import SelectIntentStep from 'components/SelectIntentStep/SelectIntentStep';
import HyperlinkRedirectStep from 'components/HyperlinkRedirectStep/HyperlinkRedirectStep';
import AttachmentUploadStep from 'components/AttachmentUploadStep';
import CustomerSatisfactionCard from 'components/CustomerSatisfactionForm/CustomerSatisfactionCard';
import TriggerEventStep from 'components/TriggerEventStep/TriggerEventStep';
import Handoff from 'components/Handoffs/Handoff';
import { EventMessage } from 'components/EventMessage/EventMessage';

//Types
import type {
  WidgetComponent,
  ContextVariables,
  ComponentTypes,
} from 'api/types';
import {
  isWorkflowTagSelectorComponent,
  isTextMessageComponent,
} from 'api/types';

//Effects
import UserMessage from 'components/UserMessage';
import { ImageStep } from 'components/ImageStep/ImageStep';
import { EmbedStep } from 'components/EmbedStep/EmbedStep';
import { ForethoughtLiveChat } from 'components/ForethoughtLiveChat/ForethoughtLiveChat';
import { DynamicCardStep } from 'components/DynamicCardStep/DynamicCardStep';
import CsatTriggerPointStep from 'components/CsatTriggerPointStep/CsatTriggerPointStep';
import { QuickFeedback } from 'components/QuickFeedback/QuickFeedback';
import InteractiveEmailInquiryStep from 'components/InteractiveEmailInquiryStep';
import { EndInteractiveEmailChatStep } from 'components/EndInteractiveEmailChatStep';
import { KustomerSatisfactionForm } from 'components/Kustomer/KustomerSatisfactionForm';
import { AnalyzingYourAttachments } from 'components/AnalyzingYourAttachments/AnalyzingYourAttachments';
import { ConversationOauthSignIn } from 'components/ConversationOauthSignIn/ConversationOauthSignIn';
import { ZendeskSipCallStep } from 'components/ZendeskSipCallStep/ZendeskSipCallStep';
import { ZendeskSipCallTriggerStep } from 'components/ZendeskSipCallTriggerStep';
import { ProductRecommendationStep } from 'components/ProductRecommendationStep/ProductRecommendationStep';
import { BrowserAgentStep } from 'components/BrowserAgentStep';

// types
type MappingExcludedComponentTypes =
  | 'DROPDOWN'
  | 'EMAIL'
  | 'LONG_TEXT'
  | 'PHONE_NUMBER'
  | 'PHONE'
  | 'NUMBER'
  | 'SHORT_TEXT'
  | 'CHECKBOX'
  | 'LIST'
  | 'DATE'
  | 'MULTI_SELECT_LIST';
type MappingComponentTypes = Exclude<
  ComponentTypes,
  MappingExcludedComponentTypes
>;

const componentMapping: Record<
  MappingComponentTypes,
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  React.FC<React.PropsWithChildren<React.ComponentProps<any>>>
> = {
  analyzing_your_attachments: AnalyzingYourAttachments,
  article: ArticleStep,
  article_suggestion: ArticleCarousel,
  attachment_analysis_upload: AttachmentUploadStep,
  attachment_upload: AttachmentUploadStep,
  autoflow_stream: () => <></>,
  brand_embassy_chat_handoff: Handoff,
  browser_agent: BrowserAgentStep,
  buttons: SingleSelect,
  comm_100_chat_handoff: Handoff,
  conversation_oauth_sign_in: ConversationOauthSignIn,
  csat_survey: CustomerSatisfactionCard,
  csat_trigger_point: CsatTriggerPointStep,
  drift_chat_handoff: Handoff,
  dynamic_card: DynamicCardStep,
  embed: EmbedStep,
  end_interactive_email_chat: EndInteractiveEmailChatStep,
  event_message: EventMessage,
  forethought_live_chat_handoff: ForethoughtLiveChat,
  form: FormStep,
  freshchat_chat_handoff: Handoff,
  generic_handoff: Handoff,
  gladly_chat_handoff: Handoff,

  gorgias_chat_handoff: Handoff,
  hubspot_chat_handoff: Handoff,
  hyperlink_redirect: HyperlinkRedirectStep,
  image: ImageStep,
  interactive_email_inquiry: InteractiveEmailInquiryStep,
  intercom_chat_handoff: Handoff,
  // This component doesn't need to render anything, it is used in thunks to
  // send a streaming request:
  knowledge_retrieval_stream: () => <></>,
  kustomer_chat_handoff: Handoff,
  kustomer_satisfaction_survey: KustomerSatisfactionForm,
  live_chat_chat_handoff: Handoff,
  open_url: OpenUrl,
  product_recommendation: ProductRecommendationStep,
  restart_conversation: SelectIntentStep,
  salesforce_chat_handoff: Handoff,
  salesforce_messaging_handoff: Handoff,
  snapengage_chat_handoff: Handoff,
  sunco_live_chat: ForethoughtLiveChat,
  talkdesk_chat_handoff: Handoff,
  text_message: MessageStep,
  trigger_event: TriggerEventStep,
  ujet_chat_handoff: Handoff,
  user_message: MessageStep,
  visible_only_to_admins: MessageStep,
  zendesk_attachment_upload: ZendeskAttachmentUploadStep,
  zendesk_chat_handoff: Handoff,
  zendesk_messaging_handoff: Handoff,
  zendesk_sip_call: ZendeskSipCallStep,
  zendesk_sip_call_trigger: ZendeskSipCallTriggerStep,
  zendesk_talk_handoff: Handoff,
};

// Component
export const GenericStepComponent = ({
  index,
  isLastStep,
  widgetComponent,
}: {
  index: number;
  isLastStep: boolean;
  widgetComponent: WidgetComponent;
}) => {
  const dispatch = useAppDispatch();
  const areLoadingDotsVisible = useAppSelector(selectAreLoadingDotsVisible);
  const workflowTags = useAppSelector(selectWorkflowTags);
  const { component_type: componentType } = widgetComponent;
  const Component = componentMapping[componentType as MappingComponentTypes];

  const isQueryingForWorkflowTag = (widgetComponent: WidgetComponent) => {
    return (
      isWorkflowTagSelectorComponent(widgetComponent) &&
      workflowTags.includes(widgetComponent.query || '')
    );
  };

  // callbacks
  const setStepCvCallback = useCallback(
    (contextVariables: ContextVariables) => {
      dispatch(
        setStepContextVariables({
          contextVariables,
          index,
        }),
      );
    },
    [dispatch, index],
  );

  const setStepFileNamesCallback = useCallback(
    (fileNames: string[]) => {
      dispatch(
        setStepFileNames({
          fileNames,
          stepIndex: index,
        }),
      );
    },
    [dispatch, index],
  );

  const setStepSentCallback = useCallback(
    (query: string) => {
      dispatch(setStepSent({ query, stepIndex: index }));
    },
    [dispatch, index],
  );

  const animate = useMemo(
    () => ({
      y: '0px',
    }),
    [],
  );
  const initial = useMemo(
    () => ({
      y: '30px',
    }),
    [],
  );
  const transition = useMemo(
    () => ({
      bounce: 0.35,
      duration: 0.4,
      type: 'spring',
    }),
    [],
  );

  if (!Component) return null;

  return (
    // Can't use <Box> here because it doesn't support `className` prop:
    <div
      className='js-generic-step-component' // Used in `useFocusReceivedMessage.ts`
      style={{
        marginInline: '-2px', // fix clipping of option buttons
        overflow: 'hidden',
        paddingInline: '2px',
      }}
    >
      <motion.div
        animate={animate}
        initial={initial}
        key={index}
        transition={transition}
      >
        <Component
          areLoadingDotsVisible={areLoadingDotsVisible}
          isLastStep={isLastStep}
          setStepContextVariables={setStepCvCallback}
          setStepFileNames={setStepFileNamesCallback}
          setStepSent={setStepSentCallback}
          stepIndex={index}
          widgetComponent={widgetComponent}
        />

        <QuickFeedback stepIndex={index} />

        {/* Free-form query: */}
        {!isQueryingForWorkflowTag(widgetComponent) &&
          widgetComponent.query && (
            <UserMessage
              stepIndex={-1}
              timestamp={
                isTextMessageComponent(widgetComponent)
                  ? widgetComponent.timestamp
                  : undefined
              }
            >
              {widgetComponent.query}
            </UserMessage>
          )}
      </motion.div>
    </div>
  );
};
